import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    HostListener,
    inject,
    Injector,
    OnInit,
    signal,
    TemplateRef,
    viewChild
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { ModalRef, SiModalService } from '@siemens/element-ng/modal';
import { ApplicationTileComponent } from '@tran-monorepo/frontend/components';
import { ApplicationTileAction } from '@tran-monorepo/frontend/components/applications/application-tile/application-tile.model';
import { calculateApplicationProgress } from '@tran-monorepo/frontend/utils';
import { Application, ApplicationStatusEnum } from '@tran-monorepo/interfaces/applications';
import { filter, switchMap, take } from 'rxjs';
import { ContentWrapperComponent } from '../../shared/components';
import { ApplicationActionsService } from './services/application-actions.service';
import {
    LoadApplications,
    LoadMoreApplications,
    ResetApplicationOverviewState
} from './state/applications-overview/applications-overview.action';
import { CustomerApplicationsOverviewState } from './state/applications-overview/applications-overview.state';

@Component({
    selector: 'tran-applications-overview',
    imports: [ApplicationTileComponent, SiLoadingSpinnerComponent, TranslatePipe, ContentWrapperComponent, RouterLink],
    templateUrl: './applications-overview.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApplicationsOverviewComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly modalService = inject(SiModalService);
    private readonly applicationActions = inject(ApplicationActionsService);
    private readonly injector = inject(Injector);
    private readonly router = inject(Router);

    private readonly deleteDraftModal = viewChild.required<TemplateRef<void>>('deleteDraftModal');
    public deleteDraftModalRef?: ModalRef;

    protected readonly applications = select(CustomerApplicationsOverviewState.applications);
    protected readonly isLoadingApplications = select(CustomerApplicationsOverviewState.isLoadingApplications);
    protected readonly hasMorePages = select(CustomerApplicationsOverviewState.hasMorePages);
    protected readonly filesByApplicationId = select(CustomerApplicationsOverviewState.filesByApplicationId);

    protected readonly progressByApplicationId = computed(
        () =>
            Object.fromEntries(
                this.applications().map(app => [
                    app.id,
                    calculateApplicationProgress(app, this.filesByApplicationId()[app.id] ?? [])
                ])
            ) as Record<string, number>
    );

    protected readonly ApplicationStatus = ApplicationStatusEnum;

    protected readonly tileActions: ApplicationTileAction[] = [
        {
            id: 'delete',
            label: 'applications.tile.delete',
            icon: 'element-delete',
            condition: (application: Application) => application.status === ApplicationStatusEnum.Draft
        }
    ];

    protected readonly isScrollable = computed(() => this.scrollHeight() > this.windowInnerHeight());
    private readonly scrollHeight = signal<number>(0);
    private readonly windowInnerHeight = signal<number>(0);

    constructor() {
        this.initializeAutoLoadEffect();
    }

    ngOnInit(): void {
        this.loadApplications();
    }

    protected onTileAction(event: { action: ApplicationTileAction; application: Application }): void {
        switch (event.action.id) {
            case 'delete':
                this.deleteApplication(event.application.id);
                break;
            default:
                console.warn(`Unknown action: ${event.action.id}`);
        }
    }

    protected deleteApplication(applicationId: string): void {
        this.deleteDraftModalRef = this.modalService.show(this.deleteDraftModal(), {
            class: 'modal-dialog-centered'
        });

        this.deleteDraftModalRef.hidden
            ?.pipe(
                take(1),
                filter(reason => reason === 'confirm'),
                switchMap(() => this.applicationActions.deleteApplication(applicationId))
            )
            .subscribe();
    }

    protected hideDeleteDraftModal(reason: 'cancel' | 'confirm'): void {
        this.deleteDraftModalRef?.hide(reason);
    }

    protected editApplication(applicationId: string): void {
        this.router.navigate(['/apply', applicationId]);
    }

    protected showApplicationDetails(applicationId: string): void {
        this.router.navigate(['/application', applicationId]);
    }

    @HostListener('window:scroll')
    protected onScroll(): void {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const threshold = 200; // Load more when 200px from bottom

        if (scrollPosition >= documentHeight - threshold && this.hasMorePages() && !this.isLoadingApplications()) {
            this.loadMoreApplications();
        }
    }

    @HostListener('window:resize')
    protected onResize(): void {
        this.updateDimensions();
    }

    private loadApplications(): void {
        this.store
            .dispatch(new ResetApplicationOverviewState())
            .pipe(
                switchMap(() => this.store.dispatch(new LoadApplications())),
                take(1)
            )
            .subscribe();
    }

    private loadMoreApplications(): void {
        this.store.dispatch(new LoadMoreApplications());
    }

    private initializeAutoLoadEffect(): void {
        effect(() => {
            const applications = this.applications();
            const isLoading = this.isLoadingApplications();

            if (!isLoading) {
                afterNextRender(() => this.updateDimensions(), { injector: this.injector });
            }

            const isScrollable = this.isScrollable();
            const hasMorePages = this.hasMorePages();

            if (applications.length && !isScrollable && hasMorePages && !isLoading) {
                this.loadMoreApplications();
            }
        });
    }

    private updateDimensions(): void {
        this.scrollHeight.set(document.documentElement.scrollHeight);
        this.windowInnerHeight.set(window.innerHeight);
    }
}
